import React, { useCallback, useState } from "react";
import styled from "styled-components";
import LoadingIndicator from "../common/ui/LoadingIndicator/LoadingIndicator";
import Collapsible from "components/common/ui/Collapsible/Collapsible";
import styles from "styles/styles";
import useTranslate from "hooks/common/useTranslate/useTranslate";
import { formatDate } from "services/common/dates/dates";
import { Event } from "interfaces/content";
import useLocale from "hooks/common/useLocale/useLocale";
import { isEmpty } from "lodash";
import Button from "components/common/ui/buttons/Button/Button";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import moment from "moment";
import { Locale } from "interfaces/urls";
import { ics } from "calendar-link";

interface Props {
    title: string;
    showAllLabel: string;
    showAllUrl: string;
    isLoading: boolean;
    events: Event[];
}

const Container = styled.div`
    margin-top: 24px;
`;

const StyledLoadingIndicator = styled(LoadingIndicator)`
    position: relative;
    display: flex;
    justify-content: center;
`;

const Header = styled.h5`
    margin: 0;
`;

const ListItem = styled.div`
    margin: ${styles.spacing[4]} 0;
    border: 1px solid ${styles.colors.grey4};
    width: 100%;
    align-items: center;
    background-color: ${styles.colors.lightGrey};

    :hover {
        border: 1px dotted ${styles.colors.red};
    }
`;

const ListItemDescription = styled.h6`
    color: ${styles.colors.grey7};
    margin: 0;
    text-align: left;
`;

const ListItemDetails = styled.p`
    color: ${styles.colors.red};
    margin: 0 0 ${styles.spacing[1]} 0;
    text-align: left;
    font-size: ${styles.fontSize.small};
`;

const ExcerptContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const EventContent = styled.div`
    padding: ${styles.spacing[4]};
`;

const ExpandedEventBlock = styled.div`
    padding: 0px 20px 10px 45px;
    white-space: pre-wrap;

    p {
        margin: 0;
    }
`;

const ShowMoreOrLessLink = styled.a`
    text-decoration: none;
    font-size: ${styles.fontSize.small};
`;

const AddToCalendarLink = styled.a`
    font-style: italic;
    font-size: ${styles.fontSize.smaller};
    color: ${styles.colors.grey7};
`;

const isNonExpandable = (event: Event, locale: Locale) =>
    isEmpty(event.details?.[locale]?.trim()) &&
    isEmpty(event.recordingUrl?.trim()) &&
    isEmpty(event.documents);

const isWithinDay = (event: Event): boolean => {
    const eventDate = moment(event.date);
    const currentDate = moment();

    return eventDate.diff(currentDate, "days") === 0;
};

const UpcomingEventsList: React.FC<Props> = ({
    title,
    events,
    isLoading,
    showAllLabel,
    showAllUrl,
}) => {
    const translate = useTranslate();
    const locale = useLocale();
    const [collapsed, setCollapsed] = useState<boolean[]>(events.map(() => true));

    const dispatch = useDispatch();

    const handleButtonClick = useCallback(() => {
        window.scrollTo(0, 0);
        dispatch(push(showAllUrl));
    }, [dispatch, showAllUrl]);

    return (
        <>
            {isLoading && (
                <Container>
                    <StyledLoadingIndicator isLoading={isLoading} />
                </Container>
            )}
            <Container>
                <Header>{title}</Header>
                {events.map((event, idx) => (
                    <ListItem
                        key={idx}
                        onClick={(e) => {
                            e.preventDefault();

                            if (isNonExpandable(event, locale)) {
                                return;
                            }

                            setCollapsed(
                                collapsed.map((value, index) => (index === idx ? !value : value))
                            );
                        }}
                    >
                        <EventContent>
                            <Collapsible
                                excerpt={
                                    <ExcerptContainer>
                                        <div>
                                            <ListItemDetails>
                                                {[formatDate(event.date, locale), event.location]
                                                    .filter(Boolean)
                                                    .join(" ")}
                                                &nbsp;(
                                                <AddToCalendarLink
                                                    onClick={(e) => e.stopPropagation()}
                                                    href={ics({
                                                        title:
                                                            event.description[locale] ??
                                                            "Fingrid Webinar",
                                                        description: event.weblink,
                                                        start: event.date,
                                                        duration: [1, "hour"],
                                                    })}
                                                    target="_blank"
                                                >
                                                    {translate("common.addToCalendar")}
                                                </AddToCalendarLink>
                                                )
                                            </ListItemDetails>
                                            <ListItemDescription>
                                                {event.description[locale] ?? ""}
                                            </ListItemDescription>
                                            <ShowMoreOrLessLink
                                                href="#"
                                                style={{
                                                    height: "0px",
                                                    visibility:
                                                        !isNonExpandable(event, locale) &&
                                                        collapsed[idx]
                                                            ? "visible"
                                                            : "collapse",
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setCollapsed(
                                                        collapsed.map((value, index) =>
                                                            index === idx ? !value : value
                                                        )
                                                    );
                                                }}
                                            >
                                                {translate("common.show.more")}
                                            </ShowMoreOrLessLink>
                                        </div>
                                        {isWithinDay(event) && (
                                            <Button
                                                onClick={() =>
                                                    window.open(event.weblink || "", "_blank")
                                                }
                                            >
                                                {translate(
                                                    "customPage.eventsAndWebinars.participate"
                                                )}
                                            </Button>
                                        )}
                                    </ExcerptContainer>
                                }
                                collapsed={collapsed[idx]}
                            >
                                <ExpandedEventBlock>
                                    <p>{event.details?.[locale] ?? event.details?.fi ?? ""}</p>
                                    <ShowMoreOrLessLink
                                        href="#"
                                        style={{
                                            visibility: collapsed[idx] ? "collapse" : "visible",
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setCollapsed(
                                                collapsed.map((value, index) =>
                                                    index === idx ? !value : value
                                                )
                                            );
                                        }}
                                    >
                                        {translate("common.show.less")}
                                    </ShowMoreOrLessLink>
                                </ExpandedEventBlock>
                            </Collapsible>
                        </EventContent>
                    </ListItem>
                ))}

                <Button onClick={handleButtonClick}>{showAllLabel}</Button>
            </Container>
        </>
    );
};

export default UpcomingEventsList;
